import React from 'react';
import { WeezHeader, Button, SplitContainer, Message, Icon, FrameCard, ToastContext } from '@weezevent/nacre';
import LoadingCard from '../../../../components/loadingCard';
import { useForm, fetcher } from '@weezevent/weezjs-core';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ContingentForm from './contingentForm';
import { colors as clrs } from '../../../../components/color-picker';
import Contingent from '../../../../models/contingentModel';
import css from '../../chart-list.module.css';

export default function ContingentCreateContainer({ organization, chartId }) {
    const { t } = useTranslation();
    const history = useHistory();
    const toast = React.useContext(ToastContext);
    const [loading, setLoading] = React.useState(false);

    const colors = [...clrs];

    const [contingents] = Contingent.useApiModel(
        {
            organizationId: organization.id,
            chartId: chartId
        },
        [organization, chartId]
    );

    const defaultColor = React.useCallback(items => {
        return Math.floor(Math.random() * items.length);
    });

    const defaultObject = React.useMemo(() => {
        return { index: 0, name: '', color: colors[defaultColor(colors)], general_admission: false, rates: [], translations: { name: {} } };
    }, [defaultColor, colors]);

    const [form, setForm] = useForm(
        {
            contingents: [{ ...defaultObject }]
        },
        {}
    );
    const handleAdd = React.useCallback(() => {
        let contingent = form.contingents;
        contingent.push({
            index: contingent.slice(-1)[0].index + 1,
            name: '',
            color: colors[defaultColor(colors)],
            general_admission: false,
            rates: [],
            translations: { name: {} }
        });
        setForm('contingents', contingent);
    }, [form.contingents, defaultColor, colors]);

    const handleDelete = React.useCallback(
        index => {
            let contingent = form.contingents;
            contingent.splice(index, 1);
            setForm('contingents', contingent);
        },
        [form.contingents]
    );

    const handleBack = React.useCallback(() => {
        history.push(`/ticket/O${organization.id}/charts/${chartId}/contingents`);
    }, [organization, chartId]);

    const handleSubmit = React.useCallback(() => {
        setLoading(true);
        fetcher
            .post(`/ticket/organizations/${organization.id}/charts/${chartId}/contingents`, { data: form.contingents })
            .then(() => {
                toast.success(t('ticket.charts.contingents.new.toast-success', { count: form.contingents.length }));
                history.push(`/ticket/O${organization.id}/charts/${chartId}/contingents`);
            })
            .catch(e => {
                toast.error(t('ticket.charts.contingents.new.toast-error'));
            })
            .finally(() => {
                setLoading(false);
            });
    }, [form.contingents, organization, chartId]);

    const existingContingentColors = React.useMemo(() => {
        return contingents?.map(contingent => contingent.color);
    }, [contingents]);

    const disableSubmit = React.useMemo(() => {
        const uniqueColorsInForm = form.contingents.map(contingent => contingent.color).filter((v, i, a) => a.indexOf(v) === i);
        return (
            !form.contingents.length ||
            !form.contingents.every(contingent => contingent.color !== null && contingent.name !== '') ||
            uniqueColorsInForm.length !== form.contingents.map(contingent => contingent.color).length ||
            existingContingentColors?.some(item => uniqueColorsInForm.includes(item))
        );
    }, [form, form.contingents, existingContingentColors]);

    return (
        <>
            <WeezHeader
                isFixed
                backLink={{
                    onClick: () => handleBack()
                }}
                rightComponent={
                    <Button
                        primary
                        className={css['category-save-btn']}
                        label={
                            <>
                                <Icon className={css['category-save-mobile-icon']} name="save" size="regular" />
                                <span className={css['category-save-mobile-label']}>{t('ticket.common.action.save')}</span>
                            </>
                        }
                        disabled={disableSubmit}
                        onClick={handleSubmit}
                    />
                }
                title={t('ticket.charts.contingents.list.add-contingent')}
            />
            <SplitContainer
                additional={
                    <Message type="tips" title={t('notifications.tips_title')} mini>
                        <p>{t('ticket.charts.contingents.new.tip-info')}</p>
                    </Message>
                }
            >
                <FrameCard title={t('ticket.creationEvent.stepSideBar.stepInfos')} subtitle={t('ticket.charts.contingents.new.subtitle')}>
                    <LoadingCard loading={loading}>
                        {form.contingents.map((contingent, index) => {
                            return (
                                <ContingentForm
                                    key={index}
                                    index={index}
                                    organization={organization}
                                    contingent={contingent}
                                    onDelete={handleDelete}
                                    form={form}
                                    setForm={setForm}
                                    existingContingentColors={existingContingentColors}
                                />
                            );
                        })}
                        <Button color="ticket" className={css['contingents-increment-btn']} inverted label={t('ticket.charts.contingents.new.title')} onClick={handleAdd} />
                    </LoadingCard>
                </FrameCard>
            </SplitContainer>
        </>
    );
}
